import classNames from 'classnames';
import { CalendarView } from 'components/domain/calendar/types/CalendarTypes';
import { IWeekDayDayPayload } from 'components/foundation/pickers/WeekDayPicker/WeekDayPicker';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { t } from 'i18next';
import { last, times } from 'lodash';
import partition from 'lodash/partition';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import { DateTime, Duration } from 'luxon';
import { DayOfWeek } from 'types/enum/DayOfWeek';
import ScheduleReminderType from 'types/enum/ScheduleReminderType';
import TimeOffType from 'types/enum/TimeOffType';
import ITimeRange from 'types/TimeRange';
import { Optional } from 'types/util/Optional';
import { getGraphQlContainsComparison, getGraphQlDoesNotContainComparison } from 'utils/apolloUtils';
import {
  convertDayOfWeekToDateTimeWeekday,
  getDateTimesBetween,
  getEndOfSundayWeek,
  getNowIsoAtUtcWithLocalTimeZone,
  getStartOfSundayWeek,
  isDateTimeInTimeRange,
  isDst,
  timeInRange,
} from 'utils/dateUtils';
import { clampMax, clampMin } from 'utils/numberUtils';
import { fieldIsSet } from 'utils/objectUtils';
import { calculateTextColorForBackground } from 'utils/styleUtils';
import { getHourMinuteFormattedString } from 'utils/timeUtils';
import { defaultSchedulingColors } from '../constants/constants';
import { SchedulingQueryParams } from '../hooks/useSchedulingQueryParams';
import {
  ISchedulingTimeOffEvent,
  RecurringDate,
  RecurringMonthlyDate,
  RecurringWeeklyDate,
  RecurringYearlyDate,
  ScheduledStatus,
  SchedulingEventType,
  SchedulingTimeOff,
  SchedulingType,
} from '../types/types';

export function getSchedulingColor(type: 'work' | 'break' | ScheduleReminderType, color?: string | null) {
  if (color) {
    return color;
  }

  // If it's not set then we default to the type default.
  return type === 'work' ? defaultSchedulingColors.work : defaultSchedulingColors.break;
}

export function combineOverlappedRanges(dates: Array<{ start: DateTime; end: DateTime }>) {
  const sorted = sortBy(dates, (d) => d.start.toSeconds());
  const first = sorted[0];

  if (!first) {
    return [];
  }

  function combine(date: { start: DateTime; end: DateTime }, index: number): Array<{ start: DateTime; end: DateTime }> {
    const nextIndex = index + 1;
    const next = sorted[nextIndex];
    if (!next) {
      return [date];
    }

    // Next is completely encapsulated, don't include
    if (next.end.toSeconds() <= date.end.toSeconds()) {
      return combine(date, nextIndex);
    } else if (next.start.toSeconds() <= date.end.toSeconds() && next.end.toSeconds() >= date.end.toSeconds()) {
      return combine({ start: date.start, end: next.end }, nextIndex);
    } else {
      return [date, ...combine(next, nextIndex)];
    }
  }

  return combine(first, 0);
}

// This is used for project/cost code/equipment since we just count pure time and overlapping doesnt matter
export function totalWorkSchedulesDatesWithoutOverlap(
  events: Array<{
    start: Date;
    end: Date;
    members: Array<{ id: string }>;
  }>
) {
  return events.reduce((acc, cur) => {
    const total = DateTime.fromJSDate(cur.end).toMillis() - DateTime.fromJSDate(cur.start).toMillis();
    return acc.plus(total * cur.members.length);
  }, Duration.fromMillis(0));
}

export function totalSchedulesDates(
  dates: Array<{
    start: Date;
    end: Date;
    type: 'work' | 'break';
  }>
): Duration {
  const [works, breaks] = partition(
    dates.map((d) => ({
      start: DateTime.fromJSDate(d.start),
      end: DateTime.fromJSDate(d.end),
      type: d.type,
    })),
    (d) => d.type === 'work'
  ).map(combineOverlappedRanges);

  return reduce<{ start: DateTime; end: DateTime }, Duration>(
    works,
    (acc, { start, end }) => {
      const startSeconds = start.toSeconds();
      const endSeconds = end.toSeconds();
      let totalSeconds = endSeconds - startSeconds;

      breaks.forEach(({ start: breakStart, end: breakEnd }) => {
        const breakStartSeconds = breakStart.toSeconds();
        const breakEndSeconds = breakEnd.toSeconds();
        const overlap = breakStartSeconds < endSeconds && breakEndSeconds > startSeconds;

        if (overlap) {
          if (breakStartSeconds >= startSeconds && breakEndSeconds <= endSeconds) {
            totalSeconds -= breakEndSeconds - breakStartSeconds;
          } else if (breakStartSeconds <= startSeconds) {
            // Break encapsulates entire entry
            totalSeconds = 0;
          } else if (breakStartSeconds >= startSeconds) {
            totalSeconds -= endSeconds - breakStartSeconds;
          } else if (breakEndSeconds <= endSeconds) {
            totalSeconds -= breakEndSeconds - breakStartSeconds;
          }
        }
      });

      if (acc === null) {
        return Duration.fromMillis(clampMin(totalSeconds, 0) * 1000);
      } else {
        return acc.plus(clampMin(totalSeconds, 0) * 1000);
      }
    },
    Duration.fromMillis(0)
  );
}

export function createSchedulingPayload(
  projectId: string | null | undefined,
  costCodeId: string | null | undefined,
  equipmentId: string | null | undefined,
  color: string | null | undefined,
  instructions: string | null | undefined
) {
  return JSON.stringify({
    project: projectId,
    cost_code: costCodeId,
    equipment: equipmentId,
    color,
    instructions,
  });
}

export function getAllMemberIdsFromScheduleReminders<T extends { links: Array<{ memberId: string }> }>(reminders: T[]) {
  return reminders.flatMap(getMemberIdsFromScheduleReminder);
}

export function getMemberIdsFromScheduleReminder<T extends { links: Array<{ memberId: string }> }>(reminder: T) {
  return reminder.links.map((link) => link.memberId);
}

export function transformTimeOffToScheduleEvent(timeOff: Omit<SchedulingTimeOff, 'cursor'>): ISchedulingTimeOffEvent {
  return {
    id: timeOff.id,
    start: DateTime.fromISO(timeOff.dateStamp).toJSDate(),
    end: DateTime.fromISO(timeOff.dateStamp).toJSDate(),
    seconds: timeOff.seconds ?? 0,
    type: timeOff.type,
    title: t('Time Off'),
    color: defaultSchedulingColors.timeOff,
    allDay: true,
    paid: timeOff.paid,
    members: [timeOff.member!],
    description: timeOff.description ?? null,
  };
}

export function isSchedulingEventTimeOff(event: SchedulingEventType): event is ISchedulingTimeOffEvent {
  return (
    event.end === undefined ||
    [TimeOffType.SICK, TimeOffType.HOLIDAY, TimeOffType.OTHER, TimeOffType.PERSONAL, TimeOffType.VACATION].some(
      (type) => event.type === type
    )
  );
}

export function getSchedulingEventStyles(time: DateTime, color: Optional<string>) {
  const dayStart = DateTime.local().startOf('day');
  const past = time < dayStart;
  const backgroundColor = color ? color : defaultSchedulingColors.work;
  const textColor = calculateTextColorForBackground(backgroundColor);

  return {
    style: {
      // if it's null or light use white
      color: textColor === 'dark' ? '#000000' : '#ffffff',
      // make the items in the past transparent
      backgroundColor: past ? backgroundColor + '66' : backgroundColor,
    },
    className: classNames({
      dark: textColor === 'dark',
      light: textColor !== 'dark',
    }),
  };
}

export function getSchedulingTimeRangeFromParamString(time: string, currentView: CalendarView) {
  const currentTimeDay = DateTime.fromFormat(time, 'M-d-yyyy', { zone: 'utc' });

  switch (currentView) {
    case 'month':
      // +/- 7 to account for blocked days
      return {
        startTime: currentTimeDay.startOf('month').minus({ days: 7 }),
        endTime: currentTimeDay.endOf('month').plus({ days: 7 }),
      };
    case 'week':
      return { startTime: getStartOfSundayWeek(currentTimeDay), endTime: getEndOfSundayWeek(currentTimeDay) };
    case 'day':
      return { startTime: currentTimeDay.startOf('day'), endTime: currentTimeDay.endOf('day') };
    default:
      throw Error(`Unsupported calendar view ${currentView}`);
  }
}

export function getSchedulingTimeOffDuration(event: Pick<ISchedulingTimeOffEvent, 'paid' | 'seconds'>) {
  return event.paid ? getHourMinuteFormattedString(event.seconds) : t('All Day');
}

const disallowedTimeOffKeys: Array<keyof SchedulingQueryParams> = [
  'colors',
  'costCodeGroupId',
  'costCodeId',
  'equipmentCategoryId',
  'equipmentId',
  'projectGroupId',
  'projectId',
];

export function shouldShowSchedulingTimeOffs(params: SchedulingQueryParams) {
  if (params.type && params.type !== 'time off') {
    return false;
  }

  return !disallowedTimeOffKeys.some((key) => fieldIsSet(params, key));
}

export function getSchedulingEventTimeOffInRangePredicate(timeRange: ITimeRange<DateTime>) {
  return (event: Pick<ISchedulingTimeOffEvent, 'start'>) => {
    const timeStamp = DateTime.fromJSDate(event.start).toLocal();
    return timeInRange(timeStamp, timeRange, true, true);
  };
}

export function isSchedulingEventUnpaidTimeOff(event: SchedulingEventType) {
  return isSchedulingEventTimeOff(event) && !event.paid;
}

export function getUniqueUnpaidTimeOffDaysCount(events: SchedulingEventType[]) {
  const uniqueDates = events.reduce((acc, cur) => {
    if (isSchedulingEventUnpaidTimeOff(cur)) {
      acc.add(cur.start.toDateString());
    }

    return acc;
  }, new Set<string>());

  return uniqueDates.size;
}

export function convertTimeOffEventToCreateTimeOffInput(
  event: Pick<ISchedulingTimeOffEvent, 'start' | 'members' | 'paid' | 'seconds' | 'type' | 'description'>,
  activeMemberId: string
) {
  const dateTime = DateTime.fromJSDate(event.start);

  return event.members.map(({ id }) => {
    return {
      dateStamp: dateTime.toISO(),
      createdOn: getNowIsoAtUtcWithLocalTimeZone(),
      createdOnDaylightSavingTime: isDst(),
      memberId: id,
      paid: event.paid,
      seconds: event.seconds,
      submitterMemberId: activeMemberId,
      type: event.type,
      description: event.description,
    } as const;
  });
}

export function getSchedulingTypeLabel(type: SchedulingType) {
  switch (type) {
    case 'work':
      return t('Scheduled Work');
    case 'time off':
      return t('Time Off');
    case 'break':
      return t('Scheduled Break');
  }
}

export function getSchedulingTypeFromString(value: Optional<string>): ScheduleReminderType {
  return value === 'work' ? ScheduleReminderType.WORK : ScheduleReminderType.BREAK;
}

export function getScheduledFilterLabel(type: ScheduledStatus) {
  switch (type) {
    case 'scheduled':
      return t('Scheduled');
    case 'unscheduled':
      return t('Unscheduled');
  }
}

export function getSchedulingIdFilterWithScheduledStatusConsideration(
  ids: string[],
  scheduledStatus: Optional<ScheduledStatus>,
  filterId: Optional<string>
) {
  if (!scheduledStatus) {
    return filterId ? { equal: filterId } : undefined;
  }

  const withFilterConsideration = filterId ? ids.filter((memberId) => memberId === filterId) : ids;
  if (scheduledStatus === 'unscheduled') {
    return getGraphQlDoesNotContainComparison(withFilterConsideration);
  } else {
    return getGraphQlContainsComparison(withFilterConsideration);
  }
}

export function getMonthDayFromRecurringDate(date: RecurringDate) {
  if (date.frequency !== 'weekly') {
    return date.dayOfMonth;
  }

  return null;
}

export function getMonthFromRecurringDate(date: RecurringDate) {
  if (date.frequency === 'yearly') {
    return date.month;
  }

  return null;
}

export function convertDaysOfWeekToWeekDayDayPayload(daysOfWeek: DayOfWeek[]): IWeekDayDayPayload[] {
  return [
    { value: DayOfWeek.SUNDAY, enabled: daysOfWeek.includes(DayOfWeek.SUNDAY) },
    { value: DayOfWeek.MONDAY, enabled: daysOfWeek.includes(DayOfWeek.MONDAY) },
    { value: DayOfWeek.TUESDAY, enabled: daysOfWeek.includes(DayOfWeek.TUESDAY) },
    { value: DayOfWeek.WEDNESDAY, enabled: daysOfWeek.includes(DayOfWeek.WEDNESDAY) },
    { value: DayOfWeek.THURSDAY, enabled: daysOfWeek.includes(DayOfWeek.THURSDAY) },
    { value: DayOfWeek.FRIDAY, enabled: daysOfWeek.includes(DayOfWeek.FRIDAY) },
    { value: DayOfWeek.SATURDAY, enabled: daysOfWeek.includes(DayOfWeek.SATURDAY) },
  ];
}

export function convertWeekDayDayPayloadToDaysOfWeek(daysOfWeek: IWeekDayDayPayload[]): DayOfWeek[] {
  return daysOfWeek.reduce((acc, cur) => {
    if (cur.enabled) {
      acc.push(cur.value);
    }

    return acc;
  }, new Array<DayOfWeek>());
}

export function getWeeklyRecurringDatesInRange(timeRange: ITimeRange<DateTime>, recurringDate: RecurringWeeklyDate) {
  const { startTime, endTime } = timeRange;
  const { endRepeat } = recurringDate;

  if (endRepeat && endRepeat.toSeconds() < startTime.toSeconds()) {
    return [];
  }

  const dateTimeWeekDays = new Set(recurringDate.days.map((day) => convertDayOfWeekToDateTimeWeekday(day)));
  const datesBetween = getDateTimesBetween(startTime, endTime);

  return datesBetween.filter((date) => {
    if (endRepeat && date.toSeconds() > endRepeat.toSeconds()) {
      return false;
    }

    return dateTimeWeekDays.has(date.weekday);
  });
}

export function getMonthlyRecurringDatesInRange(timeRange: ITimeRange<DateTime>, recurringDate: RecurringMonthlyDate) {
  const { startTime, endTime } = timeRange;
  const { endRepeat } = recurringDate;

  if (endRepeat && endRepeat.toSeconds() < startTime.toSeconds()) {
    return [];
  }

  const absoluteStartMonth = (startTime.year - 1) * 12 + startTime.month;
  const absoluteEndMonth = (endTime.year - 1) * 12 + endTime.month;
  const monthOffsets = times(absoluteEndMonth - absoluteStartMonth + 1);

  const day = clampMax(recurringDate.dayOfMonth, startTime.daysInMonth);
  const monthlyRecurringDate = startTime.set({ day });

  return monthOffsets.reduce((acc, cur) => {
    const date = monthlyRecurringDate.plus({ month: cur });
    const adjustedDate = date.daysInMonth < recurringDate.dayOfMonth ? date.endOf('month').startOf('day') : date;

    if (
      isDateTimeInTimeRange(adjustedDate, timeRange, true, true) &&
      (!endRepeat || date.toSeconds() <= endRepeat.toSeconds())
    ) {
      acc.push(adjustedDate);
    }

    return acc;
  }, new Array<DateTime>());
}

export function getYearlyRecurringDatesInRange(timeRange: ITimeRange<DateTime>, recurringDate: RecurringYearlyDate) {
  const { startTime, endTime } = timeRange;
  const { endRepeat } = recurringDate;

  if (endRepeat && endRepeat.toSeconds() < startTime.toSeconds()) {
    return [];
  }

  const years = times(endTime.year - startTime.year + 1, (year) => startTime.year + year);

  return years.reduce((acc, cur) => {
    // Need to check validity cause of leap year :)
    const date = DateTime.fromObject({ month: recurringDate.month, day: recurringDate.dayOfMonth, year: cur });

    if (
      date.isValid &&
      isDateTimeInTimeRange(date, timeRange, true, true) &&
      (!endRepeat || date.toSeconds() <= endRepeat.toSeconds())
    ) {
      acc.push(date);
    }

    return acc;
  }, new Array<DateTime>());
}

export function convertRecurringDateToDaysInRange(
  timeRange: ITimeRange<DateTime>,
  recurringDate: RecurringDate
): DateTime[] {
  const { frequency, endRepeat } = recurringDate;

  if (endRepeat && endRepeat.toSeconds() < timeRange.startTime.toSeconds()) {
    return [];
  }

  if (frequency === 'yearly') {
    return getYearlyRecurringDatesInRange(timeRange, recurringDate);
  } else if (frequency === 'monthly') {
    return getMonthlyRecurringDatesInRange(timeRange, recurringDate);
  } else if (frequency === 'weekly') {
    return getWeeklyRecurringDatesInRange(timeRange, recurringDate);
  }

  return [];
}

export function getDashboardNavigationRange(
  encompassingTimeRange: ITimeRange<DateTime>,
  individualTimeRanges: Array<ITimeRange<number>>,
  timeFrame: DashboardTimeFrame
): ITimeRange<DateTime> {
  if (timeFrame === 'pastSeven' || timeFrame === 'pastFourteen') {
    return encompassingTimeRange;
  }

  const lastOfRanges = last(individualTimeRanges);

  if (lastOfRanges) {
    return {
      startTime: DateTime.fromSeconds(lastOfRanges.startTime, { zone: 'utc' }),
      endTime: DateTime.fromSeconds(lastOfRanges.endTime, { zone: 'utc' }),
    };
  }

  return encompassingTimeRange;
}
